import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { useParams } from 'react-router-dom';
import { api } from '../apiClient';

const placementLabel = (p) => (p || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ReviewMockupPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mockupId: initialMockupId, mockup: initialMockup } = location.state || {};
  const { mockupId: urlMockupId } = useParams();

  const [mockup, setMockup] = useState(initialMockup);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);

  const sides = useMemo(() => {
    if (!mockup) return [];
    if (mockup.mockup_image_urls && mockup.mockup_image_urls.length > 0) {
      return mockup.mockup_image_urls.map(({ placement, url }) => ({ label: placementLabel(placement), imageUrl: url }));
    }
    const placements = mockup.placements && mockup.placements.length > 0
      ? mockup.placements
      : [{ placement: mockup.image_placement || 'front', image_url: null }];
    return placements.map((p, i) => ({
      label: placementLabel(p.placement),
      imageUrl: p.image_url || (i === 0 ? mockup.mockup_image_url : null) || mockup.mockup_image_url,
    }));
  }, [mockup?.placements, mockup?.image_placement, mockup?.mockup_image_url, mockup?.mockup_image_urls]);

  const placementText = useMemo(() => {
    if (mockup?.placements && mockup.placements.length > 0) {
      return mockup.placements.map((p) => placementLabel(p.placement)).join(', ');
    }
    return mockup?.image_placement ? placementLabel(mockup.image_placement) : '';
  }, [mockup?.placements, mockup?.image_placement]);

  useEffect(() => {
    const fetchMockupDetails = async () => {
      if (mockup) { // If full mockup object is already passed in state
        setLoading(false);
        return;
      }

      // Use the mockupId from the URL if it exists
      if (urlMockupId) {
        try {
          const response = await api.get(`/mockups/${urlMockupId}`);
          setMockup(response.data);
        } catch (err) {
          console.error('Error fetching mockup details:', err);
          setError(`Failed to load mockup details: ${err.response?.data?.detail || err.message}`);
        } finally {
          setLoading(false);
        }
      } else {
        setError('No mockup ID provided.');
        setLoading(false);
      }
    };

    fetchMockupDetails();
  }, [urlMockupId, mockup]);

  const handleProceedToPayment = () => {
    if (!mockup) {
      setError('Mockup data is not available to proceed to payment.');
      return;
    }

    const paymentDetails = {
      mockupId: mockup.id,
      quantity: 1, // Default to 1 for now, can be made configurable later
      totalAmount: parseFloat(mockup.price),
      currency: 'usd', // Assuming USD
      shippingAddress: mockup.shipping_address,
    };

    navigate('/payment', { state: paymentDetails });
  };

  if (loading) {
    return <div className="container mx-auto p-4 text-center">Loading mockup details...</div>;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-center text-red-500">Error: {error}</div>;
  }

  if (!mockup) {
    return <div className="container mx-auto p-4 text-center text-red-500">Mockup not found.</div>;
  }

  return (
    <SessionAuth>
      <div className="container mx-auto p-6 bg-background rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">Review Your Mockup</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Mockup Image Display with slider for all sides */}
          <div className="border border-gray-200 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Your Design</h2>
            <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4">
              <img
                src={sides[slideIndex]?.imageUrl || mockup.mockup_image_url}
                alt={sides[slideIndex] ? `${sides[slideIndex].label} view` : 'Generated Mockup'}
                className="w-full h-64 object-contain"
              />
              {sides.length > 0 && (
                <span className="absolute top-2 left-2 bg-black/60 text-white text-sm font-medium px-2 py-1 rounded">
                  {sides[slideIndex]?.label}
                </span>
              )}
              {sides.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setSlideIndex((i) => (i <= 0 ? sides.length - 1 : i - 1))}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
                    aria-label="Previous side"
                  >
                    &lt;
                  </button>
                  <button
                    type="button"
                    onClick={() => setSlideIndex((i) => (i >= sides.length - 1 ? 0 : i + 1))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
                    aria-label="Next side"
                  >
                    &gt;
                  </button>
                  <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                    {sides.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSlideIndex(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-colors ${i === slideIndex ? 'bg-gray-800 ring-1 ring-white' : 'bg-white/70 hover:bg-white'}`}
                        aria-label={`View ${sides[i]?.label}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            <p className="text-gray-600">Prompt: {mockup.prompt}</p>
            <p className="text-gray-600">Style: {mockup.style}</p>
          </div>

          {/* Mockup Details */}
          <div className="border border-gray-200 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Order Details</h2>
            <p className="text-gray-600">T-shirt Model: {mockup.tshirt_model_name}</p>
            <p className="text-gray-600">Variant: {mockup.tshirt_variant_id}</p>
            {(placementText || mockup.image_placement) && (
              <p className="text-gray-600">Placement: {placementText || placementLabel(mockup.image_placement)}</p>
            )}
            {mockup.stitch_color && <p className="text-gray-600">Stitch Color: {mockup.stitch_color}</p>}
            <p className="text-gray-800 font-bold mt-4">Final Price: ${mockup.price ? mockup.price.toFixed(2) : 'N/A'}</p>
          </div>
        </div>

        {/* Shipping Address (Read-only) */}
        <div className="border border-gray-200 rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Shipping Address</h2>
          {mockup.shipping_address ? (
            <div className="text-gray-600">
              <p>{mockup.shipping_address.first_name} {mockup.shipping_address.last_name}</p>
              <p>{mockup.shipping_address.address1}</p>
              <p>{mockup.shipping_address.city}, {mockup.shipping_address.state} {mockup.shipping_address.zip}</p>
              <p>{mockup.shipping_address.country_code}</p>
              <p>Email: {mockup.shipping_address.email}</p>
              <p>Phone: {mockup.shipping_address.phone}</p>
            </div>
          ) : (
            <p className="text-gray-600">Shipping address not available.</p>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-4 text-center max-w-2xl mx-auto">
          By paying, you confirm you have the right to use this artwork on a physical product.
          See{' '}
          <Link to="/ai-tool-terms" className="text-primary font-medium hover:underline">Third-Party AI Tool Terms</Link>
          {' '}for AI-generated and uploaded content.
        </p>

        {/* Proceed to Payment Button */}
        <button
          onClick={handleProceedToPayment}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading || !mockup || !mockup.price}
        >
          Proceed to Payment (${mockup.price ? mockup.price.toFixed(2) : '0.00'})
        </button>
      </div>
    </SessionAuth>
  );
};

export default ReviewMockupPage;